// 📄 pages/Admin/sections/OrganizationsSection.tsx
//
// Sub-fase F: reemplaza la vieja vista SSO "Organizaciones / Clientes"
// (OrganizationFormModal.tsx + getOrganizations/saveOrganization, ya
// eliminados de organizationService.ts). Esta vista es la primera
// consumidora real de la infraestructura de la Sub-fase A: muestra "mis
// organizaciones" (useOrganizations, no un list() de toda la colección),
// permite marcar cuál es la "activa" (persistida en localStorage — desde
// Sub-fase G1 también determina en qué org nacen los proyectos/clientes
// nuevos, ver OrgSwitcher.tsx en la topbar para el selector "real") y
// crear equipos nuevos.
import React, { useEffect, useState } from 'react';
import { Plus, Building2, User, Users as UsersIcon, RefreshCw, CheckCircle2, Circle, Save, ShieldAlert, Pencil, Archive, ArchiveRestore } from 'lucide-react';
import { useOrganizations } from '../../../hooks/useOrganizations.ts';
import CreateTeamOrgModal from '../../../components/organizacion/CreateTeamOrgModal.tsx';
import { renameOrganization, setOrganizationArchivada } from '../../../services/organizationService.ts';
import {
  UserProfile,
  listPlatformUsers,
  updateUserLimiteEquipos,
  updateUserLimiteProyectos,
  updateUserLimiteMiembrosPorProyecto,
  updateUserLimiteMiembrosPorOrganizacion,
  updateUserLimiteCredencialesVault,
  updateUserLimiteApiTesterMensual,
  updateUserLimiteOneBotMensual
} from '../../../services/userService.ts';
import { PLANES, PLAN_DEFAULT_ID, assignUserPlan, getPlan } from '../../../services/planService.ts';

// Celda genérica "cantidad actual / límite editable" — reusada para
// proyectos y miembros (mismo mecanismo, ver proyecto_limites_organizacion:
// cantidadX la mantiene un trigger, limiteX lo edita el Admin acá, y el
// freno real vive en firestore.rules, no en esta UI).
function LimiteCell({
  valorGuardado, onGuardar
}: { valorGuardado: number | undefined; onGuardar: (valor: number | null) => Promise<void> }) {
  const [edit, setEdit] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const valorActual = edit ?? String(valorGuardado ?? '');
  const cambiado = edit !== null && edit.trim() !== String(valorGuardado ?? '');

  const handleGuardar = async () => {
    const raw = edit?.trim() ?? '';
    const valor = raw === '' ? null : Number(raw);
    if (valor !== null && (!Number.isFinite(valor) || valor < 0)) return;
    setSaving(true);
    try {
      await onGuardar(valor);
      setEdit(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min={0}
        // "∞" y no "Sin límite": en w-20 (más el spinner del number) el
        // texto se cortaba ("Sin lími"), y ensanchar 7 columnas desborda la
        // tabla. El texto completo queda en el tooltip y para lectores.
        placeholder="∞"
        title="Vacío = sin límite"
        aria-label="Límite (vacío = sin límite)"
        value={valorActual}
        onChange={(e) => setEdit(e.target.value)}
        className="w-20 bg-zinc-800 border border-white/10 rounded-lg px-2.5 py-1.5 text-sm"
      />
      {cambiado && (
        <button
          onClick={handleGuardar}
          disabled={saving}
          className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 disabled:opacity-50"
        >
          <Save className="w-3.5 h-3.5" /> Guardar
        </button>
      )}
    </div>
  );
}

// "Planes/Suscripciones" (Fase 5/5, 2026-08-26, ver planService.ts) —
// asignar un plan sobreescribe los 4 `limiteX` de este usuario con los
// valores del plan (o los borra, si el plan trae `null` = ilimitado) Y
// las `features` (Slack, Búsqueda IA). El admin puede seguir ajustando un
// límite puntual con LimiteCell DESPUÉS de asignar el plan — no queda
// bloqueado, el plan solo fija el punto de partida.
function PlanCell({
  planId, onAsignar
}: { planId: string | undefined; onAsignar: (planId: string) => Promise<void> }) {
  const [asignando, setAsignando] = useState(false);
  const actual = planId || PLAN_DEFAULT_ID;

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nuevo = e.target.value;
    if (nuevo === actual) return;
    setAsignando(true);
    try {
      await onAsignar(nuevo);
    } finally {
      setAsignando(false);
    }
  };

  return (
    <select
      value={actual}
      onChange={handleChange}
      disabled={asignando}
      className="bg-zinc-800 border border-white/10 rounded-lg px-2.5 py-1.5 text-sm disabled:opacity-50"
    >
      {PLANES.map((p) => (
        <option key={p.id} value={p.id}>{p.nombre}</option>
      ))}
    </select>
  );
}

// Sub-sección de límites (proyecto_limites_organizacion, rediseño
// 2026-08-19): TODOS los límites anti-abuso viven en el USUARIO, no en la
// organización — un mismo usuario puede dueñar varias orgs, así que un
// tope por-org se podía esquivar creando más orgs. Reusa listPlatformUsers
// (mismo listado completo sin paginar que ya usa Soporte), no la lista
// filtrada de UsersSection.tsx (esa a propósito solo trae role:"admin", no
// sirve acá). Si la base de usuarios crece mucho, esta tabla es la primera
// candidata a paginar — no se resolvió ahora por no ser el caso todavía.
function LimitesPorUsuario() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listPlatformUsers().then(setUsers).finally(() => setLoading(false));
  }, []);

  const actualizarLocal = (uid: string, cambios: Partial<UserProfile>) =>
    setUsers(prev => prev.map(u => u.uid === uid ? { ...u, ...cambios } : u));

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden mt-8">
      <div className="p-4 border-b border-white/10 flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-amber-400" />
        <div>
          <h2 className="text-sm font-semibold text-white">Planes y límites por usuario</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Asignar un plan fija los límites de arranque y habilita Slack/Búsqueda IA (ver Plan) — los límites siguen editables abajo como excepción puntual. Un límite vacío (∞) equivale a ilimitado. "Proyectos" y "Credenciales Vault" son un tope TOTAL sumando todas sus organizaciones/proyectos; "Miembros/proyecto" y "Miembros/organización" se aplican a CADA proyecto/organización que cree, sin sumarse entre sí.
          </p>
        </div>
      </div>
      {loading ? (
        <div className="p-8 text-center text-gray-400 flex flex-col items-center gap-2">
          <RefreshCw className="w-5 h-5 animate-spin text-indigo-500" />
          <p className="text-xs">Cargando usuarios...</p>
        </div>
      ) : (
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="bg-white/5 text-gray-400 text-[11px] uppercase tracking-wider">
              <th className="p-4">Usuario</th>
              <th className="p-4 w-32">Plan</th>
              <th className="p-4">Equipos</th>
              <th className="p-4 w-36">Límite equipos</th>
              <th className="p-4">Proyectos</th>
              <th className="p-4 w-36">Límite proyectos</th>
              <th className="p-4 w-40">Límite miembros/proyecto</th>
              <th className="p-4 w-40">Límite miembros/organización</th>
              <th className="p-4">Credenciales Vault</th>
              <th className="p-4 w-40">Límite credenciales Vault</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.uid} className="border-t border-white/5">
                <td className="p-4 font-medium text-white">{u.name ? `${u.name} — ${u.email}` : u.email}</td>
                <td className="p-4">
                  <PlanCell
                    planId={u.planId}
                    onAsignar={async (planId) => {
                      await assignUserPlan(u.uid, planId);
                      const plan = getPlan(planId);
                      actualizarLocal(u.uid, {
                        planId: plan.id,
                        features: plan.features,
                        limiteEquipos: plan.limiteEquipos ?? undefined,
                        limiteProyectos: plan.limiteProyectos ?? undefined,
                        limiteMiembrosPorProyecto: plan.limiteMiembrosPorProyecto ?? undefined,
                        limiteMiembrosPorOrganizacion: plan.limiteMiembrosPorOrganizacion ?? undefined,
                        limiteCredencialesVault: plan.limiteCredencialesVault ?? undefined,
                        limiteApiTesterMensual: plan.limiteApiTesterMensual ?? undefined,
                        limiteOneBotMensual: plan.limiteOneBotMensual ?? undefined
                      });
                    }}
                  />
                </td>
                <td className="p-4 text-gray-300">{u.cantidadEquiposCreados ?? 0}</td>
                <td className="p-4">
                  <LimiteCell
                    valorGuardado={u.limiteEquipos}
                    onGuardar={async (valor) => {
                      await updateUserLimiteEquipos(u.uid, valor);
                      actualizarLocal(u.uid, { limiteEquipos: valor ?? undefined });
                    }}
                  />
                </td>
                <td className="p-4 text-gray-300">{u.cantidadProyectosCreados ?? 0}</td>
                <td className="p-4">
                  <LimiteCell
                    valorGuardado={u.limiteProyectos}
                    onGuardar={async (valor) => {
                      await updateUserLimiteProyectos(u.uid, valor);
                      actualizarLocal(u.uid, { limiteProyectos: valor ?? undefined });
                    }}
                  />
                </td>
                <td className="p-4">
                  <LimiteCell
                    valorGuardado={u.limiteMiembrosPorProyecto}
                    onGuardar={async (valor) => {
                      await updateUserLimiteMiembrosPorProyecto(u.uid, valor);
                      actualizarLocal(u.uid, { limiteMiembrosPorProyecto: valor ?? undefined });
                    }}
                  />
                </td>
                <td className="p-4">
                  <LimiteCell
                    valorGuardado={u.limiteMiembrosPorOrganizacion}
                    onGuardar={async (valor) => {
                      await updateUserLimiteMiembrosPorOrganizacion(u.uid, valor);
                      actualizarLocal(u.uid, { limiteMiembrosPorOrganizacion: valor ?? undefined });
                    }}
                  />
                </td>
                <td className="p-4 text-gray-300">{u.cantidadCredencialesCreadas ?? 0}</td>
                <td className="p-4">
                  <LimiteCell
                    valorGuardado={u.limiteCredencialesVault}
                    onGuardar={async (valor) => {
                      await updateUserLimiteCredencialesVault(u.uid, valor);
                      actualizarLocal(u.uid, { limiteCredencialesVault: valor ?? undefined });
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Mes actual en formato "YYYY-MM" (UTC) — mismo criterio que
// functions/services/monthlyQuota.js, para poder mostrar "0" en vez del
// número guardado de un mes viejo sin tener que llamar al backend.
const mesActualUTC = (): string => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

// Cuotas MENSUALES (2026-09-08, primera de 3: API Tester → OneBot →
// Validaciones XML) — tabla separada de `LimitesPorUsuario` a propósito:
// esa lista límites de por vida (nunca se resetean); esta lista consumo
// del mes en curso (se resetea perezosamente, ver monthlyQuota.js). Mezclar
// ambas en una sola tabla la iba a dejar carísima de leer a medida que se
// agreguen las próximas 2 features (2 columnas más cada una).
function UsoMensualPorUsuario() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listPlatformUsers().then(setUsers).finally(() => setLoading(false));
  }, []);

  const actualizarLocal = (uid: string, cambios: Partial<UserProfile>) =>
    setUsers(prev => prev.map(u => u.uid === uid ? { ...u, ...cambios } : u));

  const mesActual = mesActualUTC();

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden mt-6">
      <div className="p-4 border-b border-white/10 flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-amber-400" />
        <div>
          <h2 className="text-sm font-semibold text-white">Uso mensual por usuario</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Se resetea solo al empezar cada mes (la próxima vez que el usuario use la función, no con un cron). "API Tester" es un pozo compartido entre el explorador genérico, Buscar Proveedores y Buscar Facturas. "OneBot" es Nexus AI (el chat) — un mensaje bloqueado por límite se responde dentro del propio chat, no consume cuota.
          </p>
        </div>
      </div>
      {loading ? (
        <div className="p-8 text-center text-gray-400 flex flex-col items-center gap-2">
          <RefreshCw className="w-5 h-5 animate-spin text-indigo-500" />
          <p className="text-xs">Cargando usuarios...</p>
        </div>
      ) : (
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="bg-white/5 text-gray-400 text-[11px] uppercase tracking-wider">
              <th className="p-4">Usuario</th>
              <th className="p-4">API Tester (este mes)</th>
              <th className="p-4 w-40">Límite API Tester/mes</th>
              <th className="p-4">OneBot (este mes)</th>
              <th className="p-4 w-40">Límite OneBot/mes</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const usoApiTesterVigente = u.usoApiTesterMes === mesActual ? (u.usoApiTesterCantidad ?? 0) : 0;
              const usoOneBotVigente = u.usoOneBotMes === mesActual ? (u.usoOneBotCantidad ?? 0) : 0;
              return (
                <tr key={u.uid} className="border-t border-white/5">
                  <td className="p-4 font-medium text-white">{u.name ? `${u.name} — ${u.email}` : u.email}</td>
                  <td className="p-4 text-gray-300">{usoApiTesterVigente}</td>
                  <td className="p-4">
                    <LimiteCell
                      valorGuardado={u.limiteApiTesterMensual}
                      onGuardar={async (valor) => {
                        await updateUserLimiteApiTesterMensual(u.uid, valor);
                        actualizarLocal(u.uid, { limiteApiTesterMensual: valor ?? undefined });
                      }}
                    />
                  </td>
                  <td className="p-4 text-gray-300">{usoOneBotVigente}</td>
                  <td className="p-4">
                    <LimiteCell
                      valorGuardado={u.limiteOneBotMensual}
                      onGuardar={async (valor) => {
                        await updateUserLimiteOneBotMensual(u.uid, valor);
                        actualizarLocal(u.uid, { limiteOneBotMensual: valor ?? undefined });
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// "Renombrar + Archivar" (2026-08-20, a pedido del usuario): el nombre
// editable inline, solo para el OWNER de un equipo (ver firestore.rules —
// personal y no-owner ni siquiera intentan el update). No usa LimiteCell
// porque es texto libre, no un número con "sin límite" = vacío.
function NombreOrgCell({ nombre, onGuardar }: { nombre: string; onGuardar: (nuevo: string) => Promise<void> }) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(nombre);
  const [saving, setSaving] = useState(false);

  if (!editando) {
    return (
      <button
        onClick={() => { setValor(nombre); setEditando(true); }}
        className="flex items-center gap-1.5 text-white hover:text-indigo-300 transition-all"
        title="Renombrar"
      >
        {nombre}
        <Pencil className="w-3 h-3 opacity-50" />
      </button>
    );
  }

  const guardar = async () => {
    if (!valor.trim() || valor.trim() === nombre) { setEditando(false); return; }
    setSaving(true);
    try {
      await onGuardar(valor.trim());
      setEditando(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <input
        autoFocus
        value={valor}
        onChange={(e) => setValor(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') guardar(); if (e.key === 'Escape') setEditando(false); }}
        className="bg-zinc-800 border border-indigo-500/30 rounded-lg px-2 py-1 text-sm text-white focus:outline-none"
      />
      <button onClick={guardar} disabled={saving} className="text-indigo-400 hover:text-indigo-300 disabled:opacity-50">
        <Save className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function OrganizationsSection() {
  const { organizations, orgActivaId, setOrgActiva, loading, reload } = useOrganizations();
  const [modalOpen, setModalOpen] = useState(false);

  const handleCreated = () => {
    setModalOpen(false);
    reload();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light text-white">Organizaciones</h1>
          <p className="text-sm text-gray-400 mt-1">Tus organizaciones — personal y equipos.</p>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 px-4 py-2.5 rounded-xl text-sm font-semibold text-[#fff] transition-all shadow-lg"
        >
          <Plus className="w-4 h-4" />
          Crear equipo
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
          <p className="text-sm">Cargando organizaciones...</p>
        </div>
      ) : organizations.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <Building2 className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Aún no tenés organizaciones.</p>
        </div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-white/5 text-gray-400 text-[11px] uppercase tracking-wider">
                <th className="p-4">Organización</th>
                <th className="p-4">Tipo</th>
                <th className="p-4">Tu rol</th>
                <th className="p-4 w-32">Estado</th>
                <th className="p-4 w-28">Activa</th>
              </tr>
            </thead>
            <tbody>
              {organizations.map((org) => {
                const isPersonal = org.type === 'personal';
                const isActiva = org.id === orgActivaId;
                const esOwner = org.role === 'owner';
                const puedeGestionar = esOwner && !isPersonal;
                return (
                  <tr key={org.id} className="border-t border-white/5 hover:bg-white/[0.03] transition-all">
                    <td className="p-4 font-medium text-white">
                      {puedeGestionar ? (
                        <NombreOrgCell nombre={org.name} onGuardar={async (nuevo) => { await renameOrganization(org.id, nuevo); await reload(); }} />
                      ) : (
                        org.name
                      )}
                    </td>
                    <td className="p-4">
                      {isPersonal ? (
                        <span className="inline-flex items-center gap-1.5 bg-gray-500/10 text-gray-400 border border-gray-500/20 px-2.5 py-1 rounded-full text-[11px] font-semibold">
                          <User className="w-3.5 h-3.5" />
                          Personal
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 px-2.5 py-1 rounded-full text-[11px] font-semibold">
                          <UsersIcon className="w-3.5 h-3.5" />
                          Equipo
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-gray-300 capitalize">{org.role}</td>
                    <td className="p-4">
                      {isPersonal ? (
                        <span className="text-xs text-gray-600">—</span>
                      ) : org.archivada ? (
                        <span className="inline-flex items-center gap-1.5 bg-white/5 text-gray-400 border border-white/10 px-2.5 py-1 rounded-full text-[11px] font-semibold">
                          Archivada
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2.5 py-1 rounded-full text-[11px] font-semibold">
                          Activa
                        </span>
                      )}
                      {puedeGestionar && (
                        <button
                          onClick={async () => { await setOrganizationArchivada(org.id, !org.archivada); await reload(); }}
                          title={org.archivada ? 'Reactivar equipo' : 'Archivar equipo (no borra nada, se puede reactivar)'}
                          className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-white mt-1 transition-all"
                        >
                          {org.archivada ? <ArchiveRestore className="w-3 h-3" /> : <Archive className="w-3 h-3" />}
                          {org.archivada ? 'Reactivar' : 'Archivar'}
                        </button>
                      )}
                    </td>
                    <td className="p-4">
                      <button
                        onClick={() => setOrgActiva(org.id)}
                        className={`flex items-center gap-1.5 text-xs font-medium transition-all ${
                          isActiva ? 'text-indigo-400' : 'text-gray-500 hover:text-gray-300'
                        }`}
                        title={isActiva ? 'Organización activa' : 'Marcar como activa'}
                      >
                        {isActiva ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                        {isActiva ? 'Activa' : 'Marcar'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <CreateTeamOrgModal onClose={() => setModalOpen(false)} onCreated={handleCreated} />
      )}

      <LimitesPorUsuario />
      <UsoMensualPorUsuario />
    </div>
  );
}
